import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Link,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  PhotoCamera as PhotoIcon,
  ExitToApp as LogoutIcon,
  EmojiEvents as EmojiEventsIcon,
  MenuBook as MenuBookIcon,
  Download as DownloadIcon,
  WorkspacePremium as CertificateIcon,
  CardGiftcard as GiftIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import DailySpeedLeaderboard from '../../components/Students/DailySpeedLeaderboard';

const DASHBOARD_ACTION_CARDS = [
  {
    key: 'courses',
    title: 'My Courses',
    description: 'Lessons, flashcards & learning progress',
    icon: MenuBookIcon,
    background: '#334E68',
    buttonText: 'View My Courses',
    path: '/student/my-courses',
  },
  {
    key: 'certificate',
    title: 'Live Digital Certificate',
    description: 'Videos, quizzes, boost tasks & certificate preview',
    icon: CertificateIcon,
    iconColor: '#D4B886',
    background: '#4A3F5C',
    buttonText: 'View Certificate',
    path: '/student/live-certificate',
  },
  {
    key: 'leaderboard',
    title: 'Leaderboard',
    description: 'Quiz scores, typing speed, games & LMS rankings',
    icon: EmojiEventsIcon,
    background: '#7A6548',
    buttonText: 'View Leaderboard',
    path: '/student/leaderboard',
  },
  {
    key: 'referrals',
    title: 'Course Referrals',
    description: 'Promote LMS courses · WhatsApp link · track sales',
    icon: GiftIcon,
    background: '#0F766E',
    buttonText: 'Open Referrals',
    path: '/student/referrals',
  },
  {
    key: 'android',
    title: 'Android App',
    description: 'Install KNLS student app on your phone',
    icon: DownloadIcon,
    background: '#3E5C4E',
    buttonText: 'Install App',
    href: 'https://drive.google.com/uc?export=download&id=1gIJLYwEkY1h5v9joV5zwMVW--pnTw8l4',
    external: true,
  },
];

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const StudentDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [studentData, setStudentData] = useState(null);

  useEffect(() => {
    fetchStudentData();
    // Redirect to track sheet as default page
    const shouldRedirect = !window.location.pathname.includes('/dashboard') && 
                           window.location.pathname === '/student/dashboard';
    if (shouldRedirect) {
      // Small delay to show loading, then redirect
      setTimeout(() => {
        navigate('/student/track-sheet');
      }, 500);
    }
  }, [navigate]);

  const fetchStudentData = async () => {
    try {
      const token = localStorage.getItem('student_token');
      const studentDataStr = localStorage.getItem('student_data');
      
      // Check for token first - redirect immediately if not found
      if (!token) {
        setLoading(false);
        navigate('/student/login');
        return;
      }

      // Parse student data to get phone number
      let student = null;
      let phone = null;
      
      if (studentDataStr) {
        try {
          student = JSON.parse(studentDataStr);
          phone = student.phone;
          
          // Set cached data immediately for faster UI
          setStudentData(student);
        } catch (parseError) {
          console.error('Error parsing student data:', parseError);
        }
      }

      // If no phone number found, redirect to login
      if (!phone) {
        setLoading(false);
        setError('Student data not found. Please login again.');
        setTimeout(() => {
          navigate('/student/login');
        }, 2000);
        return;
      }

      // Fetch fresh data from API using phone number (public route, no auth required)
      try {
        const response = await axios.get(`${API_BASE_URL}/students/dashboard/${phone}`);
        
        if (response.data.success) {
          setStudentData(response.data.student);
          localStorage.setItem('student_data', JSON.stringify(response.data.student));
        } else {
          throw new Error(response.data.message || 'Failed to fetch student data');
        }
      } catch (apiError) {
        console.error('Error fetching student data from API:', apiError);
        // If API fails but we have cached data, show cached data with warning
        if (student) {
          toast.warning('Using cached data. Some information may be outdated.');
        } else {
          throw apiError;
        }
      }
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load student data');
      toast.error('Failed to load student data');
      
      // Redirect to login after showing error
      setTimeout(() => {
        navigate('/student/login');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_data');
    toast.success('Logged out successfully');
    navigate('/student/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Check for token first - redirect immediately if not found (prevent flash)
  const token = localStorage.getItem('student_token');
  if (!token && !loading) {
    navigate('/student/login');
    return null;
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/student/login')} sx={{ mt: 2 }}>
          Go to Login
        </Button>
      </Box>
    );
  }

  if (!studentData) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">No student data found</Alert>
        <Button onClick={() => navigate('/student/login')} sx={{ mt: 2 }}>
          Go to Login
        </Button>
      </Box>
    );
  }

  const accessSummary = studentData.courseAccessSummary || null;
  const accessStartDate = studentData.accessStartsAt || accessSummary?.starts_at || studentData.joining_date;
  const accessCourseName = accessSummary?.course_name || accessSummary?.course_code;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: 4,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={studentData.photo}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: 32,
                  }}
                >
                  {studentData.name?.charAt(0)?.toUpperCase() || <PersonIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold" gutterBottom>
                    {studentData.name || 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    Student ID: {studentData.student_id || studentData.id || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              >
                Logout
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            ⚠️ Fees once paid are non-refundable.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            By using KNLS courses and this portal, you agree to our terms, non-refundable fee policy, and disclaimers.
          </Typography>
          <Link component={RouterLink} to="/terms-and-conditions" fontWeight={600}>
            Read full Terms, Conditions & Disclaimer
          </Link>
        </Alert>

        {/* Student Details */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Personal Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PersonIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {studentData.name || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PhoneIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Mobile Number
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {studentData.phone || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocationIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        City/Village
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {studentData.city_village || studentData.city || studentData.village || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PhotoIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Photo
                      </Typography>
                      {studentData.photo ? (
                        <Avatar src={studentData.photo} sx={{ width: 60, height: 60, mt: 1 }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          No photo uploaded
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                  Course Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Enrolled Courses
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {studentData.courses && studentData.courses.length > 0 ? (
                          studentData.courses.map((course, index) => (
                            <Chip
                              key={index}
                              label={course.course_name || course.course_code}
                              color="primary"
                              size="small"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No courses enrolled
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Course Registered Date
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {formatDate(accessStartDate)}
                      </Typography>
                      {accessCourseName && (
                        <Typography variant="body2" color="text.secondary">
                          {accessCourseName}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Student ID Number
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 2,
                        display: 'inline-block',
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {studentData.student_id || studentData.id || 'N/A'}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Quick action cards — square, 4 distinct colors */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          {DASHBOARD_ACTION_CARDS.map((card) => {
            const Icon = card.icon;
            const handleCardClick = () => {
              if (card.external && card.href) {
                window.open(card.href, '_blank', 'noopener,noreferrer');
                return;
              }
              if (card.path) navigate(card.path);
            };

            return (
              <Grid item xs={6} md={3} key={card.key}>
                <Card
                  sx={{
                    aspectRatio: '1 / 1',
                    color: 'white',
                    background: card.background,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.16)',
                    },
                  }}
                  onClick={handleCardClick}
                >
                  <CardContent
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      p: { xs: 2, sm: 2.5 },
                    }}
                  >
                    <Box>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1.5,
                        }}
                      >
                        <Icon sx={{ fontSize: 28, color: card.iconColor || 'white' }} />
                      </Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ lineHeight: 1.3 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', lineHeight: 1.4 }}>
                        {card.description}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCardClick();
                      }}
                      sx={{
                        mt: 1.5,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        color: card.background,
                        fontWeight: 600,
                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.98)' },
                      }}
                    >
                      {card.buttonText}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Daily Speed Leaderboard */}
        <DailySpeedLeaderboard currentStudentPhone={studentData.phone} />

        <Box
          component="footer"
          sx={{
            mt: 4,
            py: 2,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/terms-and-conditions">
              Terms, conditions & disclaimer
            </Link>
            {' · '}
            <Link component={RouterLink} to="/privacy-policy">
              Privacy Policy
            </Link>
            {' · '}
            <Typography component="span" variant="caption" color="text.secondary">
              All fees are non-refundable.
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default StudentDashboardPage;

